import { Component, Inject, OnInit } from '@angular/core';
import { CMPS_DIALOG_DATA, CmpsDialogRef } from '@cmps/ng-uikit';
import { of } from 'rxjs';
import { catchError, switchMap, tap } from 'rxjs/operators';

import {
    DEFAULT_RECENTLY_ACTIVE,
    DEFAULT_RECENTLY_ACTIVE_DAYS,
    FocusSelectionParams,
} from '../../coaching-base/config';
import { CommitmentFocusSummaryDto } from '../../coaching-base/general';
import { CommitmentService } from '../../coaching-commitment/services/commitment.service';
import {
    takeUntilDestroy,
    TakeUntilDestroy,
} from '../../common/take-until-destroy.decorator';

import { TranscriptImportFocusDialogData } from './model/transcript-import-focus-dialog-data.model';
import { TranslationKeyMap } from './transcript-import-focus-dialog.i18n';

@Component({
    selector: 'cmps-coaching-transcript-import-focus-dialog',
    templateUrl: './transcript-import-focus-dialog.component.html',
    host: {
        class: 'cmps-coaching-transcript-import-focus-dialog',
    },
})
@TakeUntilDestroy()
export class TranscriptImportFocusDialogComponent implements OnInit {
    /** @private */
    _i18n = TranslationKeyMap;

    /** @private */
    _pending: boolean;

    /** @private */
    _items: CommitmentFocusSummaryDto[];

    constructor(
        @Inject(CMPS_DIALOG_DATA)
        private readonly _data: TranscriptImportFocusDialogData,
        private readonly _dialogRef: CmpsDialogRef<
            TranscriptImportFocusDialogComponent,
            boolean
        >,
        private readonly _commitmentService: CommitmentService,
    ) {}

    ngOnInit(): void {
        this._reload();
    }

    /** @private */
    _onCancelClick(): void {
        this._dialogRef.close(false);
    }

    /** @private */
    _onDoNotAssociateClick(): void {
        this._dialogRef.close(true);
    }

    /** @private */
    _onAssociateFocusClick(focusId: number): void {
        const item = this._items?.find((e) => e.focusId === focusId);
        this._data.context.behaviourId = item?.behaviourId;
        this._data.context.subBehaviourId = item?.subBehaviourId;
        this._dialogRef.close(true);
    }

    private _reload(): void {
        this._pending = true;
        of(null)
            .pipe(
                this._commitmentService.retrieveCommitmentConfig(),
                switchMap((config) => {
                    return of(null).pipe(
                        this._commitmentService.listFocuses(() => {
                            const params: FocusSelectionParams =
                                config.focusSelectionParams ?? {};
                            return {
                                userId: this._data.userId,
                                recentlyActive:
                                    params.recentlyActive != null
                                        ? params.recentlyActive
                                        : DEFAULT_RECENTLY_ACTIVE,
                                recentlyActiveDays:
                                    params.recentlyActiveDays != null
                                        ? params.recentlyActiveDays
                                        : DEFAULT_RECENTLY_ACTIVE_DAYS,
                            };
                        }),
                    );
                }),
                tap((ret) => {
                    ret.user.sort((a, b) =>
                        (a.behaviourFullName ?? '').localeCompare(
                            b.behaviourFullName ?? '',
                        ),
                    );
                    this._items = ret.user;
                }),
                catchError((err) => {
                    console.log(err);
                    return of(null);
                }),
                tap(() => {
                    this._pending = false;
                }),
                takeUntilDestroy(this),
            )
            .subscribe();
    }
}
